import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  doApiCheck,
  getDirectoryGroupQuery,
  getDomainsQuery,
  getGroupLabelsQuery,
  getRoleOfGroupUserPortalQuery,
  groupSettingsQuery,
  UserKeys,
} from '../../../api/users-api';
import GoogleGroups from '../../../assets/googleGroups.png';
import { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Outlet, useLocation, useNavigate } from 'react-router';
import GroupReportsMenu from './group-reports-menu';
import DeleteGroupDialog from './delete-group-dialog';
import ReportsDialog from '../../user-management/reports-dialog';
import CreateTemplateDialog from './create-template-dialog';
import ApplyTemplateDialog, {
  useApplyGroupTemplatePermission,
} from './apply-template-dialog';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Link, useParams } from 'react-router-dom';
import { useStore } from '../../state/zustand-store';
import { groupDetailPages, pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import {
  userPortalGroupDetailsPages,
  userPortalPagesConfig,
} from '../../../shared/user-portal-pages-config';
import { gPanelTiers } from '../../../shared/gPanel-tiers';
import axios from 'axios';
import TextHoverCopy from '../../../shared/text-hover-copy';
import { DirectoryGroup, Group } from '../../../../types';
import CloneGroupsDialog from '../groups/clone-group-dialog';
import EditGroupLabelsDialog from '../../../shared/update-group-labels';
import GroupLabelChips from '../../../shared/group-label-chips';
import GroupDetailsLayout from './group-details-layout';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import SharedTabCard from '../../../shared/shared-tab-card';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Assessment from '@mui/icons-material/Assessment';
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import Delete from '@mui/icons-material/Delete';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Edit from '@mui/icons-material/Edit';
import FileCopy from '@mui/icons-material/FileCopy';
import Mail from '@mui/icons-material/Mail';
import MoreVert from '@mui/icons-material/MoreVert';
import Settings from '@mui/icons-material/Settings';
import Groups from '@mui/icons-material/Groups';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

export default function GroupDetailsPage() {
  const {
    changeLogRoles,
    toolsRoles,
    directoryRoles,
    currentTier,
    usersTier,
    loggedInUser,

    limitedVisEnabled,
  } = useUserSettings();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );
  const { limitedVisDomainList, isDefault } = useUserSettings();
  const { data: allDomains } = useQuery(getDomainsQuery(userView));
  const domains = limitedVisEnabled ? limitedVisDomainList : allDomains || [];

  const [openEditLabelsDialog, setOpenEditLabelsDialog] = useState(false);
  const { setSnackbar } = useStore();

  const { groupEmail } = useParams() as { groupEmail: string };
  const {
    isLoading: isGroupLoading,
    isFetching: isGroupFetching,
    data: group,
    error,
  } = useQuery(getDirectoryGroupQuery(groupEmail, userView));

  const {
    data: groupSettings,
    isLoading: isGroupSettingsLoading,
    isFetching: isGroupSettingsFetching,
  } = useQuery(groupSettingsQuery(groupEmail, userView));
  const { data: accessGranted } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    enabled: group !== undefined && !userView,
  });

  const { isLoading: isLabelLoading, data: groupLabels } = useQuery({
    ...getGroupLabelsQuery(groupEmail, group?.id ?? ''),
    enabled: group !== undefined && accessGranted == true && !userView,
  });

  useEffect(() => {
    if (!error) return;
    if (axios.isAxiosError(error)) {
      setSnackbar({
        open: true,
        severity: 'info',
        message: `Group wasn't found in Google so we've removed it from gPanel indexing.`,
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.Groups],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupsIndexedPage],
      });
      navigate(`/${pagesConfig.groups.link}`);
    }
  }, [error]);

  const lastPathSegment = location.pathname.split('/').filter(Boolean).pop();
  const tabsValue = userView
    ? userPortalGroupDetailsPages.some((page) => page.link === lastPathSegment)
      ? lastPathSegment
      : userPortalGroupDetailsPages[0].link
    : groupDetailPages.some((page) => page.link === lastPathSegment)
    ? lastPathSegment
    : pagesConfig.groupsGeneralInformation.link;

  const [openCreateTemplate, setOpenCreateTemplate] = useState(false);
  const [openApplyTemplate, setOpenApplyTemplate] = useState(false);
  const [openCloneGroup, setOpenCloneGroup] = useState(false);
  const [openDeleteGroup, setOpenDeleteGroup] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const kebabButtonRef = useRef<HTMLButtonElement>(null);
  const [kebabAnchorEl, setKebabAnchorEl] = useState<null | HTMLElement>(null);
  const kebabOpen = Boolean(kebabAnchorEl);
  const handleKebabClose = () => setKebabAnchorEl(null);
  const [openReportsDialog, setOpenReportsDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<{
    displayName: string;
    reportType: string;
    role: string;
  } | null>(null);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  function mapDirectoryGroupToGroup(dirGroup: DirectoryGroup): Group {
    return {
      ...dirGroup,
      directMemberCount: dirGroup.directMembersCount ?? 0,
      domain: dirGroup.email.split('@')[1] || '',
    };
  }

  const isAbleToCloneGroup = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.CloneGroup.value
  );

  const { canApplyTemplate, disabledReason: applyTemplateDisabledReason } =
    useApplyGroupTemplatePermission(directoryRoles);

  const selectedGroup: Group | undefined = group
    ? mapDirectoryGroupToGroup(group)
    : undefined;

  const { data: groupRole } = useQuery(
    getRoleOfGroupUserPortalQuery(userView, loggedInUser, groupEmail)
  );

  const isLoading =
    isGroupLoading ||
    isGroupSettingsLoading ||
    isGroupFetching ||
    isGroupSettingsFetching;

  const userPortalCanDelete = groupRole == 'MANAGER' || groupRole == 'OWNER';

  const groupDetailsTab = () => {
    if (userView) {
      return userPortalGroupDetailsPages.map((page) => {
        return (
          <Tab
            key={page.name}
            value={page.link}
            component={Link}
            to={page.link}
            label={page.name}
            icon={<page.icon />}
            iconPosition="start"
            sx={{
              minHeight: '40px',
              height: '40px',
              width: 'fit-content',
            }}
          />
        );
      });
    } else {
      return groupDetailPages.map((page) => {
        const doesntHavePermission =
          !isAbleToSeeUIElement(directoryRoles, page.requiredPermission) &&
          page.requiredPermission !== '*';
        const doesntHaveTier = usersTier.rank < page.minimumTier;
        const shouldShowButDisabled = isDefault && doesntHaveTier;
        if (shouldShowButDisabled) {
          return (
            <Tab
              key={page.name}
              value={page.link}
              component={Link}
              to={page.link}
              label={
                <Tooltip
                  title={Messages.Errors.Tier.Viewing(
                    page.minimumTierDisplayValue
                  )}
                  onClick={(e) => e.preventDefault()}
                  arrow
                >
                  <span style={{ pointerEvents: 'auto' }}>{page.name}</span>
                </Tooltip>
              }
              icon={<page.icon />}
              iconPosition="start"
              sx={{
                minHeight: '40px',
                height: '40px',
                width: 'fit-content',
              }}
              disabled
            />
          );
        }

        return (
          !(doesntHavePermission || doesntHaveTier) && (
            <Tab
              key={page.name}
              value={page.link}
              component={Link}
              to={page.link}
              label={page.name}
              icon={<page.icon />}
              iconPosition="start"
              sx={{
                minHeight: '40px',
                height: '40px',
                width: 'fit-content',
              }}
            />
          )
        );
      });
    }
  };

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        ...(!userView
          ? [
              { label: ModuleList.modules.directory.name },
              {
                label: pagesConfig.groups.name,
                path: pagesConfig.groups.link,
              },
              { label: pagesConfig.groupDetails.name },
            ]
          : []),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {userView ? (
          <Box sx={{ p: 1 }}>
            <Button
              startIcon={<ArrowBack />}
              component={Link}
              to={userView ? './../..' : `/${pagesConfig.groups.link}`}
              disableElevation
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
          </Box>
        ) : null}

        <Stack
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            backgroundColor: (theme) => theme.palette.background.paper,
            cursor: 'pointer',
            overflowX: 'hidden',
          }}
          onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
        >
          <Grid
            container
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{
              px: 1,
              pt: 1,
              pb: '8px',
              flexWrap: 'nowrap',
              minWidth: 0,
              overflowX: 'hidden',
            }}
          >
            <Grid
              sx={{
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Avatar sx={{ width: 48, height: 48 }} src={GoogleGroups} />
                <Stack sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isLoading ? (
                      <Skeleton
                        variant="rounded"
                        width={200}
                        height={26}
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      group?.name && (
                        <TextHoverCopy
                          clipboardValue={group?.name}
                          value={
                            <Typography
                              noWrap
                              sx={{
                                fontWeight: 'bold',
                                fontSize: 20,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {group?.name}
                            </Typography>
                          }
                          valueType={'Group Name'}
                        />
                      )
                    )}
                    {!userView && accessGranted && (
                      <Stack spacing={1} sx={{ width: 230 }}>
                        {isLabelLoading || groupLabels === undefined ? (
                          <Skeleton variant="rounded" width={100} height={20} />
                        ) : (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems={'center'}
                          >
                            <GroupLabelChips currentLabels={groupLabels} />
                            <Tooltip
                              arrow
                              title={
                                !isAbleToSeeUIElement(
                                  directoryRoles,
                                  DirectoryRoles.EditGroupLabels.value
                                )
                                  ? Messages.Errors.Permission.Acting(
                                      DirectoryRoles.EditGroupLabels.name
                                    )
                                  : 'Edit Group Labels'
                              }
                            >
                              <Box>
                                <IconButton
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenEditLabelsDialog(true);
                                  }}
                                  disabled={
                                    !isAbleToSeeUIElement(
                                      directoryRoles,
                                      DirectoryRoles.EditGroupLabels.value
                                    ) || !groupLabels
                                  }
                                >
                                  <Edit sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Box>
                  {isLoading ? (
                    <Skeleton variant="rounded" width={250} height={20} />
                  ) : (
                    group?.email && (
                      <TextHoverCopy
                        clipboardValue={group?.email}
                        value={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? theme.palette.grey[700]
                                  : theme.palette.grey[400],
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minHeight: '24px',
                            }}
                          >
                            <Mail fontSize="inherit" sx={{ mr: 1 }} />
                            {group?.email}
                          </Box>
                        }
                        valueType={'Group Email Address'}
                        iconSx={{ fontSize: 14 }}
                      />
                    )
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid>
              <Stack direction={'row'} sx={{ mb: 'auto' }}>
                <Tooltip arrow title="Actions">
                  <span>
                    <IconButton
                      ref={kebabButtonRef}
                      id="group-actions-menu-button"
                      aria-controls={
                        kebabOpen ? 'group-actions-menu' : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={kebabOpen ? 'true' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        setKebabAnchorEl(e.currentTarget);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu
                  id="group-actions-menu"
                  anchorEl={kebabAnchorEl}
                  open={kebabOpen}
                  onClose={handleKebabClose}
                  MenuListProps={{
                    'aria-labelledby': 'group-actions-menu-button',
                  }}
                >
                  {!userView && [
                    <DisabledTooltipMenuItem
                      key="reports"
                      disabled={
                        toolsRoles.length === 0 ||
                        !group ||
                        currentTier === gPanelTiers.essentials.value
                      }
                      data-disabled-reason={
                        currentTier === gPanelTiers.essentials.value
                          ? `${Messages.Errors.Tier.Acting(
                              gPanelTiers.essentials.displayValue
                            )}`
                          : undefined
                      }
                      iconStart={<Assessment fontSize="small" />}
                      onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                        e.stopPropagation();
                        handleKebabClose();
                        setAnchorEl(kebabButtonRef.current);
                      }}
                    >
                      Reports
                    </DisabledTooltipMenuItem>,
                    <DisabledTooltipMenuItem
                      key="clone"
                      disabled={!isAbleToCloneGroup}
                      data-disabled-reason={
                        !isAbleToCloneGroup
                          ? Messages.Errors.Permission.Acting(
                              DirectoryRoles.CloneGroup.name
                            )
                          : undefined
                      }
                      iconStart={<LibraryAdd fontSize="small" />}
                      onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                        e.stopPropagation();
                        handleKebabClose();
                        setOpenCloneGroup(true);
                      }}
                    >
                      Clone
                    </DisabledTooltipMenuItem>,
                    <DisabledTooltipMenuItem
                      key="create-template"
                      disabled={
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.CreateGroupTemplate.value
                        ) || !group
                      }
                      data-disabled-reason={
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.CreateGroupTemplate.value
                        )
                          ? Messages.Errors.Permission.Acting(
                              DirectoryRoles.CreateGroupTemplate.name
                            )
                          : undefined
                      }
                      iconStart={<FileCopy fontSize="small" />}
                      onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                        e.stopPropagation();
                        handleKebabClose();
                        setOpenCreateTemplate(true);
                      }}
                    >
                      Create Template
                    </DisabledTooltipMenuItem>,
                    <DisabledTooltipMenuItem
                      key="apply-template"
                      disabled={!canApplyTemplate || !group || !groupSettings}
                      data-disabled-reason={applyTemplateDisabledReason}
                      iconStart={<AutoFixHighOutlined fontSize="small" />}
                      onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                        e.stopPropagation();
                        handleKebabClose();
                        setOpenApplyTemplate(true);
                      }}
                    >
                      Apply Template
                    </DisabledTooltipMenuItem>,
                  ]}
                  <DisabledTooltipMenuItem
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.DeleteGroup.value
                        )) ||
                      !group ||
                      (userView && !userPortalCanDelete)
                    }
                    data-disabled-reason={
                      !userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.DeleteGroup.value
                      )
                        ? Messages.Errors.Permission.Acting(
                            DirectoryRoles.DeleteGroup.name
                          )
                        : userView && !userPortalCanDelete
                        ? Messages.Errors.Permission.Generic
                        : undefined
                    }
                    iconStart={<Delete fontSize="small" />}
                    onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                      e.stopPropagation();
                      handleKebabClose();
                      setOpenDeleteGroup(true);
                    }}
                  >
                    Delete
                  </DisabledTooltipMenuItem>
                </Menu>
                <GroupReportsMenu
                  anchorEl={anchorEl}
                  open={open}
                  handleClose={handleClose}
                  setOpenReportsDialog={setOpenReportsDialog}
                  setSelectedReport={setSelectedReport}
                  toolsRoles={toolsRoles}
                />
                <Tooltip
                  arrow
                  title={isDetailsExpanded ? 'Collapse' : 'Expand'}
                >
                  <span>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDetailsExpanded(!isDetailsExpanded);
                      }}
                    >
                      <KeyboardArrowDown
                        sx={{
                          transition: 'transform 0.3s ease-in-out',
                          transform: isDetailsExpanded
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
          <Collapse in={isDetailsExpanded} timeout="auto" unmountOnExit>
            <Grid
              container
              spacing={3}
              alignItems={'center'}
              sx={{
                py: 1,
                px: 4,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {group?.id && (
                <Grid>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                    }}
                  >
                    Group ID:
                  </Typography>
                  <Box sx={{ minHeight: '26px' }}>
                    {isLoading ? (
                      <Skeleton variant="rounded" width={150} height={20} />
                    ) : (
                      <TextHoverCopy
                        clipboardValue={group?.id}
                        value={group?.id}
                        valueType="Group ID"
                        textSx={{ color: 'text.secondary' }}
                        stackSx={{ pl: 0 }}
                        iconSx={{ fontSize: 14 }}
                      />
                    )}
                  </Box>
                </Grid>
              )}
              <Grid>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.secondary',
                  }}
                >
                  <Settings
                    fontSize="inherit"
                    sx={{
                      verticalAlign: 'text-top',
                    }}
                  />{' '}
                  Admin Created:{' '}
                </Typography>
                {isLoading ? (
                  <Skeleton variant="rounded" width={60} height={20} />
                ) : (
                  <Typography
                    textTransform={'capitalize'}
                    sx={{
                      fontWeight: 300,
                      fontSize: 14,
                      color: 'text.secondary',
                      pl: 0,
                    }}
                  >
                    {group?.adminCreated ? group?.adminCreated.toString() : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.secondary',
                  }}
                >
                  <Groups
                    fontSize="inherit"
                    sx={{
                      verticalAlign: 'text-top',
                    }}
                  />{' '}
                  Direct Member Count:{' '}
                </Typography>
                {isLoading ? (
                  <Skeleton variant="rounded" width={30} height={20} />
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 300,
                      fontSize: 14,
                      color: 'text.secondary',
                      pl: 0,
                    }}
                  >
                    {group?.directMembersCount}
                  </Typography>
                )}
              </Grid>
              {!userView && (
                <Grid>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                    }}
                  >
                    <AlternateEmail
                      fontSize="inherit"
                      sx={{
                        verticalAlign: 'text-top',
                      }}
                    />{' '}
                    Aliases:{' '}
                  </Typography>

                  {isLoading ? (
                    <Skeleton variant="rounded" width={30} height={20} />
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 300,
                        fontSize: 14,
                        color: 'text.secondary',
                        pl: 0,
                      }}
                    >
                      {group?.aliases ? group.aliases.length : 0}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Collapse>
        </Stack>
        <SharedTabCard sx={{ mt: 0.5 }}>
          <Tabs
            value={tabsValue}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons="auto"
            sx={{
              mx: 1,
              '& .MuiTabs-flexContainer': { marginTop: '6px' },
              '.MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
              '& .MuiTabs-scrollButtons': {
                '&[aria-disabled="true"]': {
                  display: 'none',
                },
              },
            }}
          >
            {groupDetailsTab()}
          </Tabs>
        </SharedTabCard>
        <GroupDetailsLayout tabsValue={tabsValue}>
          <Outlet />
        </GroupDetailsLayout>

        {group && groupSettings && !userView && openCreateTemplate ? (
          <CreateTemplateDialog
            open={openCreateTemplate}
            handleClose={() => setOpenCreateTemplate(false)}
            groupEmail={group.email}
            groupSettings={groupSettings}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {group && groupSettings && !userView && openApplyTemplate ? (
          <ApplyTemplateDialog
            open={openApplyTemplate}
            handleClose={() => setOpenApplyTemplate(false)}
            groupEmail={group.email}
            groupId={group.id ?? ''}
            groupSettings={groupSettings}
            directoryRoles={directoryRoles}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {openCloneGroup && selectedGroup ? (
          <CloneGroupsDialog
            open={openCloneGroup}
            handleClose={() => setOpenCloneGroup(false)}
            selectedGroup={selectedGroup}
            domains={domains}
          />
        ) : null}
        {group && groupSettings ? (
          <DeleteGroupDialog
            open={openDeleteGroup}
            userView={userView}
            handleClose={() => setOpenDeleteGroup(false)}
            groupEmail={group.email}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {group && groupSettings && !userView && openReportsDialog ? (
          <ReportsDialog
            open={openReportsDialog}
            handleClose={() => setOpenReportsDialog(false)}
            selectedReport={selectedReport}
            sourceEmails={[group.email]}
            sourceType={'Group'}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
        {group && groupLabels && !userView && openEditLabelsDialog ? (
          <EditGroupLabelsDialog
            open={openEditLabelsDialog}
            handleClose={() => setOpenEditLabelsDialog(false)}
            groupEmail={group.email}
            groupId={group.id ?? ''}
            currentLabels={groupLabels}
            changeLogRoles={changeLogRoles}
            directoryRoles={directoryRoles}
            accessGranted={accessGranted || false}
          />
        ) : null}
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
